import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  auditApi,
  devicesApi,
  maintenanceApi,
  rolesApi,
  simTrackerApi,
  usersApi,
  type AuditQuery,
  type DeviceQuery,
  type SimQuery,
  type UserQuery,
} from '@/lib/api/endpoints';
import { sameRef } from '@/lib/api/refs';
import { normaliseMsisdn } from '@/lib/phone';
import { queryKeys } from './keys';
import type {
  CreateDeviceInput,
  CreateDocumentInput,
  CreateRecordInput,
  CreateScheduleInput,
  CreateUserInput,
  CustomRole,
  Id,
  PaginationQuery,
  Permission,
  SimTrackerSim,
  UpdateUserInput,
  User,
} from '@/types/api';

/* ── devices ──────────────────────────────────────────────────────────────── */

export function useDevices(query?: DeviceQuery) {
  return useQuery({
    queryKey: queryKeys.devices.list(query),
    queryFn: () => devicesApi.list(query),
    placeholderData: (previous) => previous,
  });
}

/**
 * The SIM estate. Session state is computed server-side against the offline
 * threshold, so this refetches on a timer rather than going stale on the page.
 */
export function useSimCards(query?: SimQuery) {
  return useQuery({
    queryKey: queryKeys.devices.sims(query),
    queryFn: () => devicesApi.sims(query),
    placeholderData: (previous) => previous,
    refetchInterval: 60_000,
  });
}

/** Carrier connectivity counts from the external SIM tracker. */
export function useSimConnectivitySummary(enabled = true) {
  return useQuery({
    queryKey: queryKeys.simTracker.summary(),
    queryFn: () => simTrackerApi.connectivitySummary(),
    enabled,
    refetchInterval: 60_000,
    retry: 1,
  });
}

/**
 * The SIM tracker's per-SIM network status, keyed by normalised phone number
 * so it can be matched against the SIM numbers recorded on devices.
 */
export function useCarrierSimStatus(enabled = true) {
  return useQuery({
    queryKey: queryKeys.simTracker.sims(),
    queryFn: () => simTrackerApi.sims(),
    enabled,
    refetchInterval: 60_000,
    retry: 1,
    select: (sims) => {
      const byNumber = new Map<string, SimTrackerSim>();
      for (const sim of sims) {
        const key = normaliseMsisdn(sim.msisdn);
        if (key) byNumber.set(key, sim);
      }
      return { sims, byNumber };
    },
  });
}

/**
 * Which tracker is fitted to a vehicle. The list endpoint cannot filter by
 * vehicle, so the whole (bounded) device list is cached once and searched here.
 */
export function useDeviceForVehicle(vehicleId: Id | null | undefined) {
  return useQuery({
    queryKey: queryKeys.devices.list({ limit: 200 }),
    queryFn: () => devicesApi.list({ limit: 200 }),
    enabled: Boolean(vehicleId),
    staleTime: 60_000,
    select: (page) => page.items.find((device) => sameRef(device.vehicleId, vehicleId)) ?? null,
  });
}

export function useCreateDevice() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDeviceInput) => devicesApi.create(input),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: queryKeys.devices.all });
      toast.success('Device provisioned');
    },
  });
}

export function useUpdateDevice() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: Id; input: Partial<Omit<CreateDeviceInput, 'imei'>> }) =>
      devicesApi.update(id, input),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: queryKeys.devices.all });
      toast.success('Device updated');
    },
  });
}

export function useAssignDevice() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, vehicleId, note }: { id: Id; vehicleId: Id | null; note?: string }) =>
      vehicleId ? devicesApi.assign(id, vehicleId, note) : devicesApi.unassign(id),
    onSuccess: (_device, variables) => {
      client.invalidateQueries({ queryKey: queryKeys.devices.all });
      client.invalidateQueries({ queryKey: queryKeys.vehicles.all });
      toast.success(variables.vehicleId ? 'Device assigned' : 'Device unassigned');
    },
  });
}

export function useDecommissionDevice() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: Id) => devicesApi.decommission(id),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: queryKeys.devices.all });
      toast.success('Device decommissioned');
    },
  });
}

/* ── users ────────────────────────────────────────────────────────────────── */

export function useUsers(query?: UserQuery) {
  return useQuery({
    queryKey: queryKeys.users.list(query),
    queryFn: () => usersApi.list(query),
    placeholderData: (previous) => previous,
  });
}

/** Drivers are just users; the drawer and vehicle forms resolve names from here. */
export function useUserDirectory() {
  return useQuery({
    queryKey: queryKeys.users.list({ limit: 200 }),
    queryFn: () => usersApi.list({ limit: 200 }),
    staleTime: 5 * 60_000,
    select: (page) => {
      const byId = new Map<Id, User>();
      for (const user of page.items) byId.set(user.id, user);
      return { items: page.items, byId };
    },
  });
}

export function useCreateUser() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateUserInput) => usersApi.create(input),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: queryKeys.users.all });
      toast.success('User invited');
    },
  });
}

export function useUpdateUser() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: Id; input: UpdateUserInput }) => usersApi.update(id, input),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: queryKeys.users.all });
      toast.success('User updated');
    },
  });
}

export function useDeactivateUser() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: Id) => usersApi.deactivate(id),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: queryKeys.users.all });
      toast.success('User deactivated');
    },
  });
}

/* ── roles ────────────────────────────────────────────────────────────────── */

export function useRoles(query?: PaginationQuery) {
  return useQuery({
    queryKey: queryKeys.roles.list(query),
    queryFn: () => rolesApi.list(query),
  });
}

export function useCreateRole() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; description?: string; permissions: Permission[] }) =>
      rolesApi.create(input),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: queryKeys.roles.all });
      toast.success('Role created');
    },
  });
}

export function useUpdateRole() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: Id; input: Partial<CustomRole> }) =>
      rolesApi.update(id, input),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: queryKeys.roles.all });
      toast.success('Role updated');
    },
  });
}

export function useDeleteRole() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: Id) => rolesApi.remove(id),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: queryKeys.roles.all });
      toast.success('Role deleted');
    },
  });
}

/* ── maintenance ──────────────────────────────────────────────────────────── */

export function useMaintenanceSchedules(query?: PaginationQuery & { vehicleId?: Id }, enabled = true) {
  return useQuery({
    queryKey: queryKeys.maintenance.schedules(query),
    queryFn: () => maintenanceApi.schedules(query),
    enabled,
  });
}

export function useMaintenanceRecords(query?: PaginationQuery & { vehicleId?: Id }, enabled = true) {
  return useQuery({
    queryKey: queryKeys.maintenance.records(query),
    queryFn: () => maintenanceApi.records(query),
    enabled,
  });
}

export function useVehicleDocuments(query?: PaginationQuery & { vehicleId?: Id }, enabled = true) {
  return useQuery({
    queryKey: queryKeys.maintenance.documents(query),
    queryFn: () => maintenanceApi.documents(query),
    enabled,
  });
}

export function useCreateSchedule() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateScheduleInput) => maintenanceApi.createSchedule(input),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: queryKeys.maintenance.all });
      toast.success('Service schedule added');
    },
  });
}

export function useCreateRecord() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRecordInput) => maintenanceApi.createRecord(input),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: queryKeys.maintenance.all });
      toast.success('Service logged');
    },
  });
}

export function useCreateDocument() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDocumentInput) => maintenanceApi.createDocument(input),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: queryKeys.maintenance.all });
      toast.success('Document uploaded');
    },
  });
}

export function useDeleteDocument() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: Id) => maintenanceApi.removeDocument(id),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: queryKeys.maintenance.all });
      toast.success('Document removed');
    },
  });
}

/* ── audit ────────────────────────────────────────────────────────────────── */

export function useAuditLogs(query?: AuditQuery) {
  return useQuery({
    queryKey: queryKeys.audit.list(query),
    queryFn: () => auditApi.list(query),
    placeholderData: (previous) => previous,
  });
}
